import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { FileUpload } from 'primereact/fileupload';
import { Toast } from 'primereact/toast';
import ClipLoader from 'react-spinners/ClipLoader';
import { ProgressBar } from 'primereact/progressbar';
import { Tag } from 'primereact/tag';
import NewsManagementService, { NewsManagementDTO, CreateNewsRequest, UpdateNewsRequest } from '../../services/news/news-management.service';
import './add-news-modal.scss';

interface AddNewsModalProps {
  visible: boolean;
  onHide: () => void;
  onSuccess?: () => void;
  onNewsCreated?: () => void;
  editingNews?: NewsManagementDTO | null;
}

const AddNewsModal: React.FC<AddNewsModalProps> = ({
  visible,
  onHide,
  onSuccess,
  onNewsCreated,
  editingNews,
}) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [totalSize, setTotalSize] = useState(0);
  const [showUploader, setShowUploader] = useState(false);
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const toast = useRef<Toast>(null);
  const fileUploadRef = useRef<FileUpload>(null);

  useEffect(() => {
    if (!visible) return;

    if (editingNews) {
      setTitle(editingNews.title || '');
      setDescription(editingNews.newsDescription || editingNews.description || '');
      setShowUploader(false);
      setImageFile(null);
      setTotalSize(0);
    } else {
      resetForm();
      setShowUploader(true);
    }

    setFormErrors({});
  }, [editingNews, visible]);

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setImageFile(null);
    setTotalSize(0);
    setFormErrors({});
  };

  const showMessage = (severity: 'success' | 'error' | 'info', summary: string, detail: string) => {
    toast.current?.show({ severity, summary, detail, life: 4000 });
  };

  /* ---------------- Validation ---------------- */

  const validateForm = () => {
    const errors: any = {};

    if (!title.trim()) errors.title = 'Title is required';
    if (!description.trim()) errors.description = 'Description is required';

  if (!imageFile) {
    if (!editingNews) {
      errors.imageFile = 'Image is required';
    }
    if (editingNews && showUploader) {
      errors.imageFile = 'Image is required';
    }
}
    setFormErrors(errors);

    if (Object.keys(errors).length) {
      showMessage('error', 'Validation Error', String(Object.values(errors)[0]));
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      if (editingNews) {
        // Update existing news
        const updateData: UpdateNewsRequest = {
          ID: editingNews.ID || editingNews.ID || 0,
          title: title.trim(),
          newsDescription: description.trim(),
          imageFile: imageFile || undefined,
        };
        const response = await NewsManagementService.updateNews(updateData);
        if (response.isSuccess) {
          showMessage('success', 'Success', response.message || 'News updated successfully');
          resetForm();
          onSuccess?.();
          onNewsCreated?.();
          onHide();
        } else {
          showMessage('error', 'Error', response.message || 'Failed to update news');
        }
      } else {
        // Create new news
        const createData: CreateNewsRequest = {
          title: title.trim(),
          newsDescription: description.trim(),
          imageFile: imageFile || undefined,
        };
        const response = await NewsManagementService.createNews(createData);
        if (response.isSuccess) {
          showMessage('success', 'Success', response.message || 'News created successfully');
          resetForm();
          onSuccess?.();
          onNewsCreated?.();
          onHide();
        } else {
          showMessage('error', 'Error', response.message || 'Failed to create news');
        }
      }
    } catch (error: any) {
      console.error('Error:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'An error occurred while saving news';
      showMessage('error', 'Error', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleHide = () => {
    resetForm();
    onHide();
  };

  const handleFileCancel = () => {
    setImageFile(null);
    setTotalSize(0);
  };

  /* ---------------- Upload ---------------- */

  const onTemplateSelect = (e: any) => {
    const file = e.files?.[0];
    if (!file) return;
    setImageFile(file);
    setTotalSize(file.size);
    if (formErrors.imageFile) {
      const newErrors = { ...formErrors };
      delete newErrors.imageFile;
      setFormErrors(newErrors);
    }
  };


  /* ---------------- Templates ---------------- */

  const headerTemplate = (options: any) => {
    const { className, chooseButton, cancelButton } = options;

    const formatted = fileUploadRef.current
      ? fileUploadRef.current.formatSize(totalSize)
      : '0 B';

  return (
    <div className={className}>
      {/* Disable ONLY Choose button */}
      <div
        style={{
          pointerEvents: imageFile ? 'none' : 'auto',
          opacity: imageFile ? 0.5 : 1
        }}
      >
        {chooseButton}
      </div>
        {cancelButton}

        <div className="flex ml-auto gap-3 align-items-center">
          <span>{formatted} / 5 MB</span>

          <ProgressBar
            value={totalSize / 50000}
            showValue={false}
            style={{ width: '10rem', height: 12 }}
          />
        </div>
      </div>
    );
  };

  const itemTemplate = (file: any, props: any) => {
    return (
      <div className="upload-item">
        <div className="upload-left">
          <img src={file.objectURL} className="upload-thumb" />

          <div className="upload-info">
            <span className="upload-name" title={file.name}>
              {file.name}
            </span>
            <small className="upload-date">
              {new Date().toLocaleDateString('en-IN')}
            </small>

          </div>
        </div>

        <Tag value={props.formatSize} severity="warning" className="upload-size" />

        <Button
          icon="pi pi-times"
          className="p-button-text p-button-danger upload-remove"
          onClick={() => props.onRemove()}
        />
      </div>
    );
  };

  const emptyTemplate = () => (
    <div className="flex flex-column align-items-center">
      <i className="pi pi-image p-5" style={{ fontSize: '4rem' }} />
      <span>Drag & Drop Image</span>
    </div>
  );

  /* ---------------- Render ---------------- */

  return (
    <>
      <Toast ref={toast} />
      <Dialog
        visible={visible}
        onHide={handleHide}
        header={editingNews ? 'Edit News' : 'Add New News'}
        modal
        className="add-news-dialog"
        style={{ width: '90vw', maxWidth: 600 }}
      >
        <div className="add-news-form">
          {/* Title Field */}
          <div className="field">
            <label htmlFor="title" className="font-semibold">
              Title <span className="text-red-500">*</span>
            </label>
            <div className="field-input-group">
              <InputText
                id="title"
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  if (formErrors.title) {
                    const newErrors = { ...formErrors };
                    delete newErrors.title;
                    setFormErrors(newErrors);
                  }
                }}
                placeholder="Enter news title"
                className={`w-full ${formErrors.title ? 'p-invalid' : ''}`}
              />
            </div>
            {formErrors.title && <small className="error-message">{formErrors.title}</small>}
          </div>

          {/* Description Field */}
          <div className="field">
            <label htmlFor="description" className="font-semibold">
              Description <span className="text-red-500">*</span>
            </label>
            <div className="field-input-group">
              <InputTextarea
                id="description"
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                  if (formErrors.description) {
                    const newErrors = { ...formErrors };
                    delete newErrors.description;
                    setFormErrors(newErrors);
                  }
                }}
                placeholder="Enter news description"
                rows={5}
                className={`w-full ${formErrors.description ? 'p-invalid' : ''}`}
              />
            </div>
            {formErrors.description && <small className="error-message">{formErrors.description}</small>}
          </div>

          {/* Image Upload Field */}
          <div className="field">
            <label htmlFor="image" className="font-semibold">
              Image {!editingNews && <span className="text-red-500">*</span>}
            </label>

            {/* Current Image */}
            {editingNews && editingNews.imageUrl && !showUploader && (
              <div className="current-image-wrapper">
                <img
                  src={editingNews.imageUrl}
                  className="current-image"
                  alt="Current"
                />

                <div className="image-actions">
                  <Button
                    label="Replace Image"
                    icon="pi pi-refresh"
                    className="p-button-sm p-button-outlined"
                    onClick={() => setShowUploader(true)}
                  />
                </div>
              </div>
            )}

            {/* Upload */}
            {(!editingNews || showUploader) && (
              <FileUpload
                ref={fileUploadRef}
                accept="image/*"
                maxFileSize={5000000}
                multiple={false}
                customUpload

                onSelect={onTemplateSelect}
                onClear={handleFileCancel}
                onRemove={handleFileCancel}

                headerTemplate={headerTemplate}
                itemTemplate={itemTemplate}
                emptyTemplate={emptyTemplate}
              />

            )}

            {formErrors.imageFile && (
              <small className="error-message">{formErrors.imageFile}</small>
            )}
          </div>

          {/* Action Buttons */}
          <div className="dialog-buttons">
            <Button
              label="Cancel"
              icon="pi pi-times"
              onClick={handleHide}
              className="p-button-secondary"
              disabled={loading}
            />
            <Button
              label={editingNews ? 'Update' : 'Create'}
              icon={loading ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
              onClick={handleSubmit}
              disabled={loading}
              className="p-button-success"
            />
          </div>

          {loading && (
            <div className="loading-overlay">
              <ClipLoader color="#4caf50" size={40} />
              <p>{editingNews ? 'Updating news...' : 'Creating news...'}</p>
            </div>
          )}
        </div>
      </Dialog>
    </>
  );
};

export default AddNewsModal;
